import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getSession, onAuthStateChange, signOut, verifyOtp } from "@/api/auth";
import { fetchProfileWizardStatus, fetchUserRoleMaybe } from "@/api/profiles";
import { fetchSupportAdminRow } from "@/api/support";
import { logger } from "@/lib/logger";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthBenefitsPanel } from "@/components/auth/AuthBenefitsPanel";
import { LoginForm } from "@/components/auth/LoginForm";
import { SignupForm } from "@/components/auth/SignupForm";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { EmployeeSetupForm } from "@/components/auth/EmployeeSetupForm";

type Mode = "login" | "signup" | "employee-setup" | "forgot-password";

const Auth = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlMode = searchParams.get("mode");
  const initialMode: Mode =
    urlMode === "employee-setup"
      ? "employee-setup"
      : urlMode === "forgot-password"
        ? "forgot-password"
        : urlMode === "signup"
          ? "signup"
          : "login";

  const [mode, setModeState] = useState<Mode>(initialMode);
  const [employeeSetupNoSession, setEmployeeSetupNoSession] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);

  const setMode = useCallback(
    (next: Mode) => {
      setModeState(next);
      const params = new URLSearchParams(searchParams);
      if (next === "login") params.delete("mode");
      else params.set("mode", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Preload Employee dashboard chunk in the background for fast post-login.
  useEffect(() => {
    const idle =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (window as Window).requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 800);
    const handle = idle(() => {
      void import("./EmployeeDashboard");
    });
    return () => {
      if (typeof window !== "undefined" && "cancelIdleCallback" in window && typeof handle === "number") {
        (window as Window).cancelIdleCallback(handle);
      }
    };
  }, []);

  const redirectByRole = useCallback(
    async (userId: string) => {
      const [{ data: roleData }, { data: rawProfile }, { data: supportAdminRow }] = await Promise.all([
        fetchUserRoleMaybe(userId),
        fetchProfileWizardStatus(userId),
        fetchSupportAdminRow(userId),
      ]);
      const profileData = rawProfile as { wizard_completed_at: string | null } | null;

      if (roleData?.role === "employee") {
        navigate("/employee");
        return;
      }
      if (supportAdminRow) {
        navigate("/support");
        return;
      }
      if (roleData?.role === "franchisee" && !profileData?.wizard_completed_at) {
        navigate("/onboarding");
        return;
      }
      navigate("/dashboard");
    },
    [navigate],
  );

  const isPostLogout = searchParams.get("logged_out") === "true";
  const isSessionExpired = searchParams.get("session_expired") === "true";
  const skipAutoRedirectRef = useRef(isPostLogout || isSessionExpired);
  const isEmployeeSetupRef = useRef(initialMode === "employee-setup");

  useEffect(() => {
    if (isSessionExpired) {
      toast.success("Session expirée", { description: "Vous avez été déconnecté après 30 minutes d'inactivité." });
    }
  }, [isSessionExpired]);

  useEffect(() => {
    const {
      data: { subscription },
    } = onAuthStateChange(async (_event, session) => {
      if (session?.user && !skipAutoRedirectRef.current && !isEmployeeSetupRef.current) {
        redirectByRole(session.user.id);
      }
      if (skipAutoRedirectRef.current) {
        setTimeout(() => {
          skipAutoRedirectRef.current = false;
        }, 1500);
      }
    });

    getSession().then(async ({ data: { session } }) => {
      if (mode === "employee-setup") {
        const tokenHash = searchParams.get("token_hash");
        const type = searchParams.get("type");
        if (!tokenHash || type !== "signup") {
          setEmployeeSetupNoSession(true);
          return;
        }
        setIsVerifyingToken(true);
        try {
          // Un lien d'invitation active le compte du salarié invité, jamais
          // celui déjà connecté sur l'appareil (gérant resté connecté sur le
          // poste du magasin, ou sur le téléphone d'un proche). Avant ce
          // garde, une session existante court-circuitait la vérification du
          // jeton : le formulaire appelait `updateUser` sur cette session,
          // remplaçait le mot de passe du gérant par celui choisi par le
          // salarié, puis l'envoyait sur /employee — c'est-à-dire sur le
          // tableau de bord du gérant. On ferme donc la session locale, puis
          // on vérifie le jeton comme s'il n'y avait jamais eu personne.
          if (session?.user) {
            const { error: signOutError } = await signOut("local");
            if (signOutError) {
              logger.warn("Employee setup: local sign-out before token verification failed", {
                message: signOutError.message,
              });
            }
          }
          const { error } = await verifyOtp({ token_hash: tokenHash, type: "signup" });
          if (error) {
            // Expected UX case (expired/invalid invite link) — handled by the "Lien expiré" screen.
            // Log as warn to avoid polluting Sentry with non-actionable errors.
            logger.warn("Employee setup token verification failed", { message: error.message });
            setEmployeeSetupNoSession(true);
          }
        } catch (err) {
          logger.warn("Employee setup token verification threw", {
            message: err instanceof Error ? err.message : String(err),
          });
          setEmployeeSetupNoSession(true);
        } finally {
          setIsVerifyingToken(false);
        }
        return;
      }
      if (session?.user && !skipAutoRedirectRef.current) {
        redirectByRole(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, redirectByRole, mode, searchParams]);

  if (mode === "signup") {
    return (
      <AuthShell intensity="festive" layout="split" aside={<AuthBenefitsPanel />}>
        <SignupForm onSwitchMode={(m) => setMode(m)} />
      </AuthShell>
    );
  }

  if (mode === "forgot-password") {
    return (
      <AuthShell intensity="sober" layout="centered">
        <ForgotPasswordForm onBackToLogin={() => setMode("login")} />
      </AuthShell>
    );
  }

  if (mode === "employee-setup") {
    return (
      <AuthShell intensity="sober" layout="centered">
        <EmployeeSetupForm
          isVerifyingToken={isVerifyingToken}
          noSession={employeeSetupNoSession}
          onSwitchMode={(m) => {
            setEmployeeSetupNoSession(false);
            setMode(m);
          }}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell intensity="sober" layout="centered">
      <LoginForm onSwitchMode={(m) => setMode(m)} />
    </AuthShell>
  );
};

export default Auth;
